import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ShieldCheck, CreditCard, MapPinned } from 'lucide-react';
import { Reveal, useCountUp } from '../motion';

const FEATURES = [
  {
    icon: Clock,
    titleKey: 'landing.why1Title',
    titleFallback: 'Instant booking',
    descKey: 'landing.why1Desc',
    descFallback: 'Book any time with live availability and instant confirmation.',
  },
  {
    icon: ShieldCheck,
    titleKey: 'landing.why2Title',
    titleFallback: 'Verified providers',
    descKey: 'landing.why2Desc',
    descFallback: 'Every clinic, salon and spa is licensed and manually reviewed.',
  },
  {
    icon: CreditCard,
    titleKey: 'landing.why3Title',
    titleFallback: 'Secure payments',
    descKey: 'landing.why3Desc',
    descFallback: 'Pay safely with Knet, MyFatoorah or major cards.',
  },
  {
    icon: MapPinned,
    titleKey: 'landing.why4Title',
    titleFallback: 'Wide network',
    descKey: 'landing.why4Desc',
    descFallback: 'Hundreds of trusted locations across Kuwait.',
  },
];

const STATS = [
  { value: 50, labelKey: 'landing.statsProviders', labelFallback: 'Verified providers' },
  { value: 1200, labelKey: 'landing.statsAppointments', labelFallback: 'Appointments booked' },
  { value: 3500, labelKey: 'landing.statsUsers', labelFallback: 'Happy users' },
];

const StatItem = ({ stat, delay }) => {
  const { t } = useTranslation();
  const [ref, value] = useCountUp(stat.value);

  return (
    <Reveal delay={delay} className="text-center sm:text-start">
      <p
        ref={ref}
        className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white numeric-ltr"
      >
        {value.toLocaleString('en-US')}+
      </p>
      <p className="mt-2 text-sm text-white/60">{t(stat.labelKey, stat.labelFallback)}</p>
    </Reveal>
  );
};

const WhyChoose = () => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="why-choose-section"
      className="relative overflow-hidden bg-gray-950 py-20 sm:py-24 lg:py-28 grain"
    >
      <span className="aurora-blob w-[30rem] h-[30rem] -top-32 start-[-6rem] bg-blue-600/40 animate-aurora" />
      <span className="aurora-blob w-[26rem] h-[26rem] bottom-[-8rem] end-[-4rem] bg-emerald-500/35 animate-aurora-slow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-400">
            {t('landing.whyLabel', 'Why EnayaHub')}
          </p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            {t('features.title', 'Why Choose EnayaHub?')}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/60">
            {t('features.subtitle', 'The easiest way to manage your health and beauty appointments')}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Reveal key={feature.titleKey} delay={i * 110}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 lift hover:bg-white/[0.07] hover:border-white/20">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 border border-white/10">
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <h3 className="font-display mt-5 text-lg font-bold text-white">
                    {t(feature.titleKey, feature.titleFallback)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">
                    {t(feature.descKey, feature.descFallback)}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div
          data-testid="stats-band"
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8 sm:p-10"
        >
          {STATS.map((stat, i) => (
            <StatItem key={stat.labelKey} stat={stat} delay={i * 120} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
